import type { ReactNode } from 'react';

/**
 * A brand-aligned shimmer skeleton. Looks like three hairline rule
 * bars at progressively narrower widths — quiet, no spinners (the
 * brand brief leans away from spinners and motion).
 */
export function LoadingState({ label }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" aria-label={label ?? 'Loading'} className="py-6 animate-pulse">
      <div className="h-3 bg-bg-section w-3/4 mb-3 rounded-sm" />
      <div className="h-3 bg-bg-section w-1/2 mb-3 rounded-sm" />
      <div className="h-3 bg-bg-section w-2/3 rounded-sm" />
      {label && <p className="sr-only">{label}</p>}
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div className="py-10 text-center">
      <p className="text-fg-default text-body">{title}</p>
      {hint && <p className="text-fg-muted text-small mt-2 max-w-[40ch] mx-auto">{hint}</p>}
    </div>
  );
}

export function ErrorState({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="py-10 text-center" role="alert">
      <p className="text-fg-default text-body">{title}</p>
      {detail && <p className="text-fg-muted text-small mt-2 mono">{detail}</p>}
    </div>
  );
}

export function UnauthorizedState() {
  // The CF Access logout URL clears the cookie; re-hitting the
  // dashboard then bounces through the Entra sign-in dance and
  // reissues a fresh cookie.
  const reload = () => {
    window.location.href =
      'https://millboard.cloudflareaccess.com/cdn-cgi/access/logout?returnTo=' +
      encodeURIComponent(window.location.origin + '/dashboard');
  };
  return (
    <div className="py-10 text-center" role="alert">
      <p className="text-fg-default text-body mb-1">Your session has ended.</p>
      <p className="text-fg-muted text-small mb-6">
        Cloudflare Access needs a fresh cookie. This will sign you out and back in.
      </p>
      <button
        type="button"
        onClick={reload}
        className="bg-fg-default text-mb-spring-wood px-5 py-2 text-eyebrow"
      >
        Sign in again
      </button>
      <p className="text-fg-muted text-caption mt-6">
        If the same screen shows again after signing in, the worker has a stricter view of
        your identity than CF Access does — check the wrangler tail for an{' '}
        <code className="mono">access_denied</code> log line with a <code className="mono">reason</code>.
      </p>
    </div>
  );
}
